import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "../utils/axios";
import { useBotInfo } from "../contexts/BotContext";
import Navbar from "../components/Navbar";
import Spinner from "../components/Spinner";
import "./Dashboard.css";

interface User {
  id: string;
  username: string;
  discriminator: string;
  avatar: string | null;
  isAdmin?: boolean;
}

interface Guild {
  id: string;
  name: string;
  icon: string | null;
  memberCount: number;
}

interface DashboardProps {
  user: User;
}

function Dashboard({ user }: DashboardProps) {
  const [guilds, setGuilds] = useState<Guild[]>([]);
  const [loading, setLoading] = useState(true);
  const [botStatus, setBotStatus] = useState<"online" | "offline">("offline");
  const [inviteAllowed, setInviteAllowed] = useState(true);
  const { botInfo } = useBotInfo();

  useEffect(() => {
    axios
      .get("/api/guilds")
      .then((response) => {
        setGuilds(response.data.guilds);
        setBotStatus(response.data.botReady ? "online" : "offline");
      })
      .catch((error) => {
        console.error("Error fetching guilds:", error);
      })
      .finally(() => {
        setLoading(false);
      });

    // Check if bot invites are allowed
    axios
      .get("/api/bot/invite-url")
      .then((response) => {
        setInviteAllowed(response.data.allowed !== false);
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          setInviteAllowed(false);
        }
      });
  }, []);

  const getGuildIcon = (guild: Guild) => {
    if (guild.icon) {
      return `https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`;
    }
    return null;
  };

  return (
    <div className="dashboard">
      <Navbar user={user} />

      <div className="container">
        <div className="card">
          <h1>🎮 {botInfo?.username || "Bot"} Dashboard</h1>
          <p>Welcome to your Discord bot control panel, {user.username}!</p>

          <div className="stats">
            <div className="stat-card">
              <h3>Total Servers</h3>
              <div className="stat-value">{guilds.length}</div>
            </div>
            <div className="stat-card">
              <h3>Bot Status</h3>
              <div className="stat-value">
                {botStatus === "online" ? "✅ Online" : "⏳ Starting"}
              </div>
            </div>
          </div>

          <h2>📋 Servers</h2>

          {loading ? (
            <Spinner size="medium" text="Loading servers..." />
          ) : (
            <div className="guild-grid">
              {guilds.map((guild) => (
                <Link
                  to={`/server/${guild.id}`}
                  key={guild.id}
                  className="guild-card-link"
                >
                  <div className="guild-card">
                    <div className="guild-icon">
                      {getGuildIcon(guild) ? (
                        <img src={getGuildIcon(guild)!} alt={guild.name} />
                      ) : (
                        <div className="guild-icon-placeholder">
                          {guild.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="guild-info">
                      <h3>{guild.name}</h3>
                      <p>👥 {guild.memberCount} members</p>
                      <p>📝 ID: {guild.id}</p>
                    </div>
                  </div>
                </Link>
              ))}
              {inviteAllowed && (
                <Link to="/add-bot" className="guild-card-link">
                  <div className="guild-card add-bot-card">
                    <div className="guild-icon add-bot-icon">➕</div>
                    <div className="guild-info">
                      <h3>Add Bot to New Server</h3>
                      <p>Invite the bot to manage more Discord servers</p>
                    </div>
                  </div>
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
